import { Chip } from '@mui/material'
import { FC } from 'react'
import { useRecordContext, useTranslate } from 'react-admin'
import { xxllncColor } from '../layout/theme/color'

export const SuccessChip: FC = () => {
  const record = useRecordContext<{ is_success: boolean }>()
  if (!record) return null
  return (
    <Chip
      label={record.is_success ? 'OK' : 'Fout'}
      size="small"
      sx={{
        fontWeight: 600, fontSize: 11, height: 22, border: 'none',
        bgcolor: record.is_success ? xxllncColor.opGreen10 : xxllncColor.opRed10,
        color: record.is_success ? xxllncColor.green100 : xxllncColor.red100,
      }}
    />
  )
}

export const EnabledChip: FC = () => {
  const record = useRecordContext<{ enabled: boolean }>()
  if (!record) return null
  return (
    <Chip
      label={record.enabled ? 'Actief' : 'Inactief'}
      size="small"
      sx={{
        fontWeight: 600, fontSize: 11, height: 22, border: 'none',
        bgcolor: record.enabled ? xxllncColor.opGreen10 : 'rgba(0,0,0,0.04)',
        color: record.enabled ? xxllncColor.green100 : 'rgba(0,0,0,0.35)',
      }}
    />
  )
}

const METHOD_COLORS: Record<string, string> = {
  GET: xxllncColor.green100,
  POST: xxllncColor.dark100,
  PUT: xxllncColor.orange100,
  DELETE: xxllncColor.red100,
  PATCH: xxllncColor.yellow100,
  HEAD: 'rgba(0,0,0,0.45)',
  OPTIONS: 'rgba(0,0,0,0.45)',
}

export const MethodChip: FC = () => {
  const record = useRecordContext<{ http_method: string }>()
  if (!record) return null
  const c = METHOD_COLORS[record.http_method] ?? 'rgba(0,0,0,0.45)'
  return (
    <Chip label={record.http_method} size="small" variant="outlined" sx={{
      fontWeight: 600, fontSize: 11, height: 22, fontFamily: 'monospace',
      borderColor: `${c}33`, color: c,
    }} />
  )
}

export const SEVERITY_COLORS: Record<string, string> = {
  critical: xxllncColor.red100,
  warning: xxllncColor.orange100,
  info: xxllncColor.dark100,
}

export const SeverityChip: FC<{ severity?: string }> = ({ severity }) => {
  const translate = useTranslate()
  if (!severity) return null
  // eslint-disable-next-line security/detect-object-injection
  const c = SEVERITY_COLORS[severity] ?? 'rgba(0,0,0,0.45)'
  return (
    <Chip
      label={translate(`data.severity_${severity}`, { _: severity })}
      size="small"
      sx={{
        fontWeight: 600, fontSize: 11, height: 22, border: 'none',
        bgcolor: `${c}14`, color: c,
      }}
    />
  )
}

const TIER_COLORS: Record<string, { bg: string; fg: string }> = {
  enterprise: { bg: xxllncColor.opDark10, fg: xxllncColor.dark100 },
  team: { bg: xxllncColor.opDark10, fg: xxllncColor.dark100 },
  free: { bg: xxllncColor.opGreen10, fg: xxllncColor.green100 },
  none: { bg: 'rgba(0,0,0,0.04)', fg: 'rgba(0,0,0,0.35)' },
}

export const TierChip: FC<{ tier?: string }> = ({ tier: rawTier }) => {
  const tier = rawTier ?? 'none'
  // eslint-disable-next-line security/detect-object-injection
  const c = TIER_COLORS[tier] ?? TIER_COLORS.none
  return (
    <Chip
      label={tier.charAt(0).toUpperCase() + tier.slice(1)}
      size="small"
      sx={{
        fontWeight: 600, fontSize: 11, height: 22, border: 'none',
        bgcolor: c.bg, color: c.fg,
      }}
    />
  )
}

export const MarketplaceChip: FC = () => {
  const record = useRecordContext<{ marketplace: boolean }>()
  const translate = useTranslate()
  if (!record) return null
  return (
    <Chip
      label={record.marketplace ? translate('filters.marketplaceVisible') : translate('filters.marketplaceHidden')}
      size="small"
      sx={{
        fontWeight: 600, fontSize: 11, height: 22, border: 'none',
        bgcolor: record.marketplace ? xxllncColor.opGreen10 : 'rgba(0,0,0,0.04)',
        color: record.marketplace ? xxllncColor.green100 : 'rgba(0,0,0,0.35)',
      }}
    />
  )
}

export const OPERATOR_LABELS: Record<string, string> = {
  gt: '>',
  lt: '<',
  gte: '>=',
  lte: '<=',
}

const AUTH_TYPE_COLORS: Record<string, string> = {
  password: xxllncColor.dark100,
  auth0: xxllncColor.orange100,
}

export const AuthTypeChip: FC<{ label?: string; source?: string }> = () => {
  const record = useRecordContext<{ auth_type: string }>()
  const translate = useTranslate()
  if (!record) return null
  // eslint-disable-next-line security/detect-object-injection
  const color = AUTH_TYPE_COLORS[record.auth_type] ?? 'rgba(0,0,0,0.45)'
  const label = record.auth_type === 'password'
    ? translate('data.authTypePassword')
    : record.auth_type === 'auth0'
      ? translate('data.authTypeAuth0')
      : record.auth_type
  return (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      sx={{
        fontWeight: 500, fontSize: 11, height: 22,
        borderColor: `${color}4D`, color,
      }}
    />
  )
}

const VISIBILITY_COLORS: Record<string, string> = {
  personal: 'rgba(0,0,0,0.45)',
  shared: xxllncColor.green100,
}

export const VisibilityChip: FC<{ label?: string; source?: string }> = () => {
  const record = useRecordContext<{ visibility: string }>()
  const translate = useTranslate()
  if (!record) return null
  // eslint-disable-next-line security/detect-object-injection
  const color = VISIBILITY_COLORS[record.visibility] ?? 'rgba(0,0,0,0.45)'
  const label = record.visibility === 'personal'
    ? translate('data.visibilityPersonal')
    : record.visibility === 'shared'
      ? translate('data.visibilityShared')
      : record.visibility
  return (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      sx={{
        fontWeight: 500, fontSize: 11, height: 22,
        borderColor: `${color}4D`, color,
      }}
    />
  )
}

const getDocumentStatus = (record: {
  expires_at?: string | null
  single_use?: boolean
  downloaded?: boolean
}): { key: string; color: string } => {
  if (record.expires_at && new Date(record.expires_at) < new Date()) {
    return { key: 'data.statusExpired', color: xxllncColor.red100 }
  }
  if (record.single_use && record.downloaded) {
    return { key: 'data.statusDownloaded', color: 'rgba(0,0,0,0.45)' }
  }
  return { key: 'data.statusAvailable', color: xxllncColor.green100 }
}

export const DocumentStatusChip: FC<{ label?: string }> = () => {
  const record = useRecordContext<{ expires_at?: string | null; single_use?: boolean; downloaded?: boolean }>()
  const translate = useTranslate()
  if (!record) return null
  const { key, color } = getDocumentStatus(record)
  return (
    <Chip
      label={translate(key)}
      size="small"
      sx={{
        fontWeight: 600, fontSize: 11, height: 22, border: 'none',
        bgcolor: `${color}14`, color,
      }}
    />
  )
}

const ACCESS_STATUS_COLORS: Record<string, string> = {
  pending: xxllncColor.orange100,
  approved: xxllncColor.green100,
  denied: xxllncColor.red100,
}

export const AccessStatusChip: FC<{
  status: string
  i18nKeyPrefix?: string
}> = ({ status, i18nKeyPrefix = 'accessRequest' }) => {
  const translate = useTranslate()
  // eslint-disable-next-line security/detect-object-injection
  const c = ACCESS_STATUS_COLORS[status] ?? 'rgba(0,0,0,0.45)'
  return (
    <Chip
      label={translate(`${i18nKeyPrefix}.status.${status}`, { _: status })}
      size="small"
      sx={{
        fontWeight: 600, fontSize: 11, height: 22, border: 'none',
        bgcolor: `${c}14`, color: c,
      }}
    />
  )
}
